import { useState } from 'react';
import { RiInformationLine } from 'react-icons/ri';

import { useAudioPlayerStore } from '@/music/hooks/use-audio-player-store';
import { useGenerationInfoStore } from '@/music/hooks/use-generation-info-store';
import { isSameTrack, type GenerationInfo, type Music, type PlayerTrack } from '@/music/types';
import { AudioTypeSelector } from './audio-type-selector';
import { DownloadButton } from './download-button';
import { PlayPauseButton } from './play-pause-button';

interface MusicCardProps {
  music: Music;
  title: string;
  generation: GenerationInfo | null;
}

export const MusicCard = ({ music, title, generation }: MusicCardProps) => {
  const [audioType, setAudioType] = useState<'full' | 'highlight'>('full');
  const audioTypeOptions = music.audios.map((audio) => ({
    value: audio.type,
    label: audio.type === 'full' ? '전체' : '하이라이트',
  }));
  const currentAudio = music.audios.find((audio) => audio.type === audioType) || music.audios[0];

  const track: PlayerTrack = {
    url: currentAudio.url,
    title: currentAudio.type === 'highlight' ? `${title} (하이라이트)` : title,
    generation,
  };

  const isTrackPlaying = useAudioPlayerStore(
    (state) => state.activeTrack != null && isSameTrack(state.activeTrack, track) && state.isPlaying
  );
  const play = useAudioPlayerStore((state) => state.play);
  const pause = useAudioPlayerStore((state) => state.pause);
  const openInfo = useGenerationInfoStore((state) => state.open);

  return (
    <div className="flex w-full items-center gap-3 rounded-xl border bg-white p-3">
      <PlayPauseButton
        isPlaying={isTrackPlaying}
        onClick={() => (isTrackPlaying ? pause() : play(track))}
      />
      <div className="flex min-w-0 grow flex-col gap-1">
        <p className="truncate text-sm font-medium">{title}</p>
        {audioTypeOptions.length > 1 && (
          <AudioTypeSelector value={audioType} onChange={setAudioType} options={audioTypeOptions} />
        )}
      </div>
      {generation && (
        <button
          onClick={() => openInfo(track)}
          aria-label="생성 정보"
          title="생성 정보"
          className="text-muted-foreground hover:bg-accent hover:text-foreground flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors"
        >
          <RiInformationLine className="text-lg" />
        </button>
      )}
      <DownloadButton music={music} />
    </div>
  );
};
